import React, { useContext, useEffect, useState } from "react";
import PropTypes from "prop-types";
import useChat from "../../../hooks/useChat";
import useUsers from "../../../hooks/useUsers";
import useTickets from "../../../hooks/useTickets";
import { PAGE_STATE, langComponent } from "../../../constants";

import { ReactComponent as MagnifyingGlassSvg } from "../../../assets/icons/chat/magnifying-glass.svg";
import { ReactComponent as MaximizeSvg } from "../../../assets/icons/chat/maximize.svg";
import { ReactComponent as MinimizeSvg } from "../../../assets/icons/chat/minimize.svg";
import { ReactComponent as SessionPinSvg } from "../../../assets/chat/session-pin.svg";
import { ReactComponent as OptionMenuSvg } from "../../../assets/chat/option-menu.svg";
import { ReactComponent as NoMessagesSvg } from "../../../assets/chat/no-messages.svg";

const assetsUri = import.meta.env.ASSETS_URI;

import ChatSessionContext from "../../../contexts/ChatSessionContext";
import SupportTicket from "./SupportTicket";
import AppContext from "../../../AppContext";
import { toast } from "react-toastify";
import { fromNow } from "../../../helpers/common.helper";
import _ from "lodash";

const Sessions = ({
  userId,
  onSelectSession,
  mini,
  onClose,
  onMaximize,
  support,
}) => {
  const chatSessionContext = useContext(ChatSessionContext);
  const [pageState, setPageState] = useState(PAGE_STATE.LOADING);
  const [currentTab, setCurrentTab] = useState(
    chatSessionContext.isSupport ? 2 : 1
  );
  const { querySessions, unpinSession, pinSession, hideSession } = useChat();
  const { queryUsers, queryStaffs } = useUsers();
  const { user, viewTicket, getLang, setUser, confirm } =
    useContext(AppContext);
  const Translation = getLang(langComponent.chat);
  const [search, setSearch] = useState("");
  const [isToggledOptions, setIsToggledOptions] = useState(undefined);

  useEffect(() => {
    setPageState(PAGE_STATE.IDLE);

    // if (support) {
    //   getSupportSessions();
    //   return;
    // }
  }, []);

  /**
   * Get all support sessions
   */
  const getSupportSessions = async () => {
    // * Get sessions
    let _sessions = [];
    const _userIds = [];
    const _staffIds = [];

    const { data: sessionData } = await querySessions(
      { staff: { $ne: null }, support: true, closed: true },
      1,
      10
    );

    _sessions = sessionData.map((d) => {
      const endUser = d.users[0];
      _userIds.push(endUser);
      _staffIds.push(d.staff);

      d.endUserId = endUser;
      d.staffId = d.staff;

      return d;
    });

    const [userResults, staffResults] = await Promise.allSettled([
      queryUsers({ _id: { $in: _userIds } }, 1, _userIds.length),
      queryStaffs({ _id: { $in: _staffIds } }, 1, _staffIds.length),
    ]);

    if (
      userResults.status !== "fulfilled" ||
      staffResults.status !== "fulfilled"
    ) {
      toast.error("Unable to get user data");
      return;
    }

    // * Users
    const _usersFound = userResults.value.data;
    const _staffsFound = staffResults.value.data;

    _sessions = _sessions.map((s) => {
      const userMatch = _usersFound.find((u) => u._id === s.endUserId);
      const staffMatch = _staffsFound.find((u) => u._id === s.staffId);

      delete s.endUserId;
      s.endUser = userMatch;

      delete s.staffUd;
      s.staff = staffMatch;

      return s;
    });
    chatSessionContext.setSessions(_sessions);
    initUserConnectivity(_sessions);
    setPageState(PAGE_STATE.IDLE);
  };

  const handleClickSession = (_id) => {
    onSelectSession(_id, user?.company ?? user?._id);
  };

  const handleClose = () => onClose();

  const handleMaximize = () => onMaximize();

  const handleSearchChange = ({ target }) => {
    setSearch(target.value);
  };

  const handleDeleteSession = (sessionId) => {
    // * Hide session
    chatSessionContext.hideSession(sessionId);
  };

  const handlePinSession = (id) => {
    setIsToggledOptions(undefined);
    chatSessionContext.pinSession(id);
  };

  const handleUnpinSession = (id) => {
    setIsToggledOptions(undefined);
    chatSessionContext.unpinSession(id);
  };

  const getContent = () => {
    if (currentTab === 1) {
      return (
        <>
          <div className="chat-sessions-search">
            <MagnifyingGlassSvg />
            <input
              type="text"
              className="chat-sessions-search__input"
              placeholder={Translation("Search by name")}
              value={search}
              onChange={handleSearchChange}
            />
          </div>
          {chatSessionContext.sessions?.map((session) => {
            const {
              _id,
              endUser: {
                _id: endUserId,
                isCompany,
                logo,
                avatar,
                name,
                firstname,
                surname,
                connectivity,
              },
              staffId,
              staff,
              message,
              unread,
              isPinned,
              hideSince,
            } = session;

            const fullName = !isCompany ? `${firstname} ${surname}` : name;

            if (
              search &&
              !fullName.toLowerCase().includes(search.toLowerCase())
            ) {
              return <></>;
            }

            const avatarSrc = !isCompany ? avatar : logo;
            let lastMessage = message?.msg;

            if (message.images > 0) {
              lastMessage = "Sent an image";
            }

            if (message.attachments > 0) {
              lastMessage = "Sent a file";
            }

            if (message.location) {
              lastMessage = "Sent a location";
            }

            return (
              <div
                className="chat-sessions-session"
                onClick={() => handleClickSession(_id)}
                key={`session-${session._id}`}
                role="button"
                onMouseLeave={() => setIsToggledOptions(undefined)}
              >
                <div className="chat-sessions-session-avatar">
                  <img
                    className="chat-sessions-session-avatar__image"
                    alt="avatar"
                    src={`${assetsUri}/${avatarSrc}`}
                    onError={({ target }) => {
                      target.src = avatarSrc;
                    }}
                  />
                  {connectivity?.online && (
                    <div className="chat-sessions-session-avatar__online" />
                  )}
                  {isPinned && (
                    <SessionPinSvg className="chat-sessions-session-avatar__pinned" />
                  )}
                </div>
                <div
                  className={`chat-sessions-session-name ${
                    unread ? "chat-sessions-session-name--new" : ""
                  }`}
                  style={{
                    gridRow: !lastMessage ? "1 / 3" : "1",
                  }}
                >
                  {fullName}
                </div>
                {support ? (
                  <div className="chat-sessions-session-staff">
                    {staff ? `${staff.firstname} ${staff.lastname}` : staffId}
                  </div>
                ) : (
                  message &&
                  lastMessage && (
                    <div className="chat-sessions-session-message">
                      <div
                        className={`chat-sessions-session-message-msg ${
                          unread ? "chat-sessions-session-message-msg--new" : ""
                        }`}
                      >
                        {lastMessage}
                      </div>
                      <div
                        className="chat-sessions-session-message-last-msg-date"
                        style={{
                          gridRow: !lastMessage ? "1 / 3" : "1",
                        }}
                      >
                        {fromNow(message.createdOn).toString()}
                      </div>
                    </div>
                  )
                )}
                {unread && <div className="chat-sessions-session-new-dot" />}
                {!user.support && (
                  <OptionMenuSvg
                    className="chat-sessions-session-option"
                    onClick={(e) => {
                      e.stopPropagation();
                      setIsToggledOptions((curr) => (curr ? undefined : _id));
                    }}
                  />
                )}
                {isToggledOptions === _id && (
                  <div className="chat-sessions-session-options">
                    {isPinned && (
                      <div
                        className="chat-sessions-session-options__item"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleUnpinSession(_id);
                        }}
                      >
                        Unpin
                      </div>
                    )}
                    {!isPinned && (
                      <div
                        className="chat-sessions-session-options__item"
                        onClick={(e) => {
                          e.stopPropagation();
                          handlePinSession(_id);
                        }}
                      >
                        Pin
                      </div>
                    )}
                    <div
                      className="chat-sessions-session-options__item"
                      onClick={(e) => {
                        e.stopPropagation();

                        confirm(
                          "Delete chat?",
                          "If you delete this chat. You won't be able to restore its history",
                          [
                            {
                              id: "cancel",
                              label: "Cancel",
                              variant: "secondary",
                            },
                            {
                              id: "delete",
                              label: "Delete",
                              variant: "primary",
                            },
                          ],
                          (action) => {
                            if (action.id === "cancel") return;

                            handleDeleteSession(_id);

                            // * Unpin session if is pinned
                            if (!isPinned) return;
                            handleUnpinSession(_id);
                          }
                        );
                      }}
                    >
                      Delete
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </>
      );
    }

    if (currentTab === 2) {
      return <SupportTicket onViewTicket={viewTicket} />;
    }
  };

  if (pageState === PAGE_STATE.LOADING) {
    return <>Loading</>;
  }

  return (
    <div className="chat-sessions">
      {mini && (
        <div className="chat-sessions-mini-header">
          <div className="chat-sessions-mini-header-ctas">
            <div
              className="chat-sessions-mini-header-ctas__icon"
              onClick={handleClose}
            >
              <MinimizeSvg />
            </div>
            {!chatSessionContext.isSupport && (
              <div
                className="chat-sessions-mini-header-ctas__icon chat-sessions-mini-header-ctas__icon--maximize"
                onClick={handleMaximize}
              >
                <MaximizeSvg />
              </div>
            )}
          </div>
        </div>
      )}
      <div className="chat-sessions-content">
        {getContent()}
        <div className="chat-sessions-content__empty">
          <NoMessagesSvg width={160} />
          <span>No messages found</span>
        </div>
      </div>
    </div>
  );
};

Sessions.propTypes = {
  userId: PropTypes.string,
  onSelectSession: PropTypes.func,
  mini: PropTypes.bool,
  onClose: PropTypes.func,
  onMaximize: PropTypes.func,
  support: PropTypes.bool,
};

Sessions.defaultProps = {
  userId: "",
  onSelectSession: (_id) => {},
  support: false,
};

export default Sessions;
